import { getAuth, signInAnonymously, onAuthStateChanged, User, createUserWithEmailAndPassword, signOut as firebaseSignOut, sendPasswordResetEmail } from 'firebase/auth';
import { getDatabase, ref as dbRef, onValue, push, set, off, get, update } from 'firebase/database';
import { AmslerGridData, GridDot } from '../types/AmslerGridData';
import { getStorage, ref as storageRef, listAll, deleteObject } from 'firebase/storage';
import { child, serverTimestamp } from 'firebase/database';
import { secureLogger } from '../utils/loggerUtils';

export type EyeType = 'left' | 'right' | 'all';
export type DateRange = { start: number; end: number };

export interface FirebaseServiceInterface {
  // User management
  createUser(email: string, password: string): Promise<User>;
  signIn(): Promise<User>;
  signOut(): Promise<void>;
  resetPassword(email: string): Promise<void>;
  isUserSignedIn(): boolean;
  getCurrentUserId(): string | null;
  // Grid data management
  saveGrid(grid: Omit<AmslerGridData, 'id'>): Promise<string>;
  getUserGrids(options?: {
    page?: number;
    limit?: number;
    dateFrom?: number;
    dateTo?: number;
    eyeType?: string;
    isPremium?: boolean;
  }): Promise<{
    grids: AmslerGridData[];
    total: number;
    hasMore: boolean;
  }>;
  getGridById(gridId: string): Promise<AmslerGridData | null>;
  deleteGrid(gridId: string): Promise<void>;
  // Grid image generation
  generateGridImage(grid: AmslerGridData): string;
  // Data cleanup
  checkAndMigrateOldData(): Promise<boolean>;
  cleanupLegacyData(): Promise<void>;
  cleanupSpecificPath(specificUserPath: string): Promise<boolean>;
  deleteAllGrids(): Promise<void>;
  cleanupInvalidGridData(): Promise<void>;
  isDatabaseEmpty(): Promise<boolean>;
}

class FirebaseService implements FirebaseServiceInterface {
  private static instance: FirebaseService;
  private auth = getAuth();
  private db = getDatabase();
  private currentUser: User | null = null;

  private constructor() {
    onAuthStateChanged(this.auth, (user) => {
      this.currentUser = user;
    });
  }

  public static getInstance(): FirebaseService {
    if (!FirebaseService.instance) {
      FirebaseService.instance = new FirebaseService();
    }
    return FirebaseService.instance;
  }

  public async createUser(email: string, password: string): Promise<User> {
    try {
      const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
      return userCredential.user;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  public async signOut(): Promise<void> {
    try {
      await firebaseSignOut(this.auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  public async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(this.auth, email);
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  }

  public isUserSignedIn(): boolean {
    return this.currentUser !== null;
  }

  public getCurrentUserId(): string | null {
    return this.currentUser?.uid || null;
  }

  public async signIn(): Promise<User> {
    try {
      const result = await signInAnonymously(this.auth);
      return result.user;
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  }

  public async saveGrid(grid: Omit<AmslerGridData, 'id'>): Promise<string> {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      // Use Android app path structure
      const gridsRef = dbRef(this.db, `users/${this.currentUser.uid}/grids`);
      const newGridRef = push(gridsRef);
      const gridId = newGridRef.key;

      if (!gridId) {
        throw new Error('Failed to generate grid ID');
      }

      // Ensure dots and curves arrays are valid
      const dots = Array.isArray(grid.dots) ? grid.dots : [];
      const curves = Array.isArray(grid.curves) ? grid.curves : [];

      // Add ID and user ID to the grid data
      const gridWithId = {
        ...grid,
        id: gridId,
        userId: this.currentUser.uid,
        dots,
        curves
      };

      // Save the grid
      await set(newGridRef, gridWithId);
      secureLogger.log(`Grid saved successfully with ID: ${gridId}`);
      
      return gridId;
    } catch (error) {
      secureLogger.error('Error saving grid:', error);
      throw error;
    }
  }

  /**
   * Fetches a limited number of user grids with pagination and filtering
   * @param options Pagination and filtering options
   * @returns Promise that resolves to the filtered and paginated grid data
   */
  public async getUserGrids(options?: {
    page?: number;
    limit?: number;
    dateFrom?: number;
    dateTo?: number;
    eyeType?: string;
    isPremium?: boolean;
  }): Promise<{
    grids: AmslerGridData[];
    total: number;
    hasMore: boolean;
  }> {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      // Log current state
      secureLogger.log(`getUserGrids called with options: ${JSON.stringify(options)}`);
      secureLogger.log(`Current user: ${this.currentUser.uid}`);
      secureLogger.log(`Database URL: ${this.db.app.options.databaseURL}`);
      secureLogger.log(`Emulator mode: ${localStorage.getItem('useFirebaseEmulator')}`);

      // Apply defaults if options not provided
      const page = options?.page || 0;
      const limit = options?.isPremium ? (options?.limit || 10) : 10; // Non-premium users are limited to 10 results
      const offset = page * limit;
      
      // Only check canonical path
      const canonicalPath = `users/${this.currentUser.uid}/grids`;
      secureLogger.log(`Checking canonical path: ${canonicalPath}`);
      const allGrids = await this.getGridsFromPath(canonicalPath);
      
      if (allGrids.length === 0) {
        secureLogger.log('No grids found in canonical path');
        return { grids: [], total: 0, hasMore: false };
      }
      
      secureLogger.log(`Found ${allGrids.length} grids in canonical path`);
      return this.processGridsWithFilters(allGrids, options);
    } catch (error) {
      console.error('Error fetching user grids:', error);
      throw error;
    }
  }

  /**
   * Process all grids with filtering, sorting, and pagination
   * @param allGrids All grids to process
   * @param options Filtering and pagination options
   * @returns Processed grids with pagination info
   */
  private processGridsWithFilters(
    allGrids: AmslerGridData[], 
    options?: {
      page?: number;
      limit?: number;
      dateFrom?: number;
      dateTo?: number;
      eyeType?: string;
      isPremium?: boolean;
    }
  ): { grids: AmslerGridData[]; total: number; hasMore: boolean } {
    const page = options?.page || 0;
    const limit = options?.isPremium ? (options?.limit || 10) : 10;
    const offset = page * limit;
    
    // Normalize the data first
    const normalizedGrids = this.normalizeGridData(allGrids);
    
    // Return empty result if no valid grids
    if (normalizedGrids.length === 0) {
      secureLogger.log('No valid grids after normalization, returning empty result');
      return { grids: [], total: 0, hasMore: false };
    }
    
    // Apply filters
    let filteredGrids = [...normalizedGrids];
    
    // Filter by date range if specified
    if (options?.dateFrom) {
      filteredGrids = filteredGrids.filter(grid => 
        grid.timestamp >= (options.dateFrom || 0)
      );
    }
    
    if (options?.dateTo) {
      filteredGrids = filteredGrids.filter(grid => 
        grid.timestamp <= (options.dateTo || Date.now())
      );
    }
    
    // Filter by eye type if specified
    if (options?.eyeType && options.eyeType !== 'all') {
      filteredGrids = filteredGrids.filter(grid => 
        grid.eyeTested === options.eyeType
      );
    }
    
    // Sort by timestamp (newest first)
    filteredGrids.sort((a, b) => b.timestamp - a.timestamp);
    
    // Filter out any invalid entries
    filteredGrids = filteredGrids.filter(grid => 
      grid && grid.id && typeof grid.id === 'string' && grid.id.trim() !== ''
    );
    
    // Get total count
    const total = filteredGrids.length;
    
    // Apply pagination
    const paginatedGrids = filteredGrids.slice(offset, offset + limit);
    secureLogger.log(`Returning ${paginatedGrids.length} grids after pagination (total: ${total})`);
    
    return {
      grids: paginatedGrids,
      total,
      hasMore: offset + limit < total
    };
  }

  /**
   * Helper method to normalize grid data and ensure all fields are properly formatted
   * @param grids The array of grid data to normalize
   * @returns Normalized array of grid data
   */
  private normalizeGridData(grids: AmslerGridData[]): AmslerGridData[] {
    if (!Array.isArray(grids)) {
      console.warn('normalizeGridData received non-array input:', grids);
      return [];
    }
    
    secureLogger.log(`Normalizing ${grids.length} grid items`);
    const result: AmslerGridData[] = [];
    
    for (const grid of grids) {
      if (!grid || typeof grid !== 'object') {
        console.warn('Skipping invalid grid item (not an object)');
        continue;
      }
      
      // Skip placeholders or fake data
      if (!grid.id || !grid.timestamp) {
        console.warn('Skipping grid without ID or timestamp');
        continue;
      }
      
      try {
        // Ensure timestamp is a number
        let timestamp = grid.timestamp;
        if (typeof timestamp !== 'number') {
          timestamp = typeof timestamp === 'string' ? parseInt(timestamp, 10) : Date.now();
          if (isNaN(timestamp)) {
            console.warn('Skipping grid with invalid timestamp');
            continue; // Skip grids with invalid timestamps
          }
        }
        
        // Process dots safely
        const dots: GridDot[] = [];
        const now = Date.now();
        
        // Normalize dots if they exist and are an array
        if (grid.dots && Array.isArray(grid.dots)) {
          for (const dot of grid.dots) {
            if (!dot || typeof dot !== 'object') continue;
            
            // Safely extract x and y
            const x = typeof dot.x === 'number' ? dot.x : 
                     (typeof dot.x === 'string' ? parseFloat(dot.x) : NaN);
            const y = typeof dot.y === 'number' ? dot.y : 
                     (typeof dot.y === 'string' ? parseFloat(dot.y) : NaN);
            
            // Only include valid coordinates
            if (!isNaN(x) && !isNaN(y) && x >= 0 && x <= 1 && y >= 0 && y <= 1) {
              dots.push({
                x,
                y,
                timestamp: typeof dot.timestamp === 'number' ? dot.timestamp : now
              });
            }
          }
        }

        // Process curves safely
        const curves = [];
        
        // Normalize curves if they exist and are an array
        if (grid.curves && Array.isArray(grid.curves)) {
          for (const curve of grid.curves) {
            if (!curve || typeof curve !== 'object' || !Array.isArray(curve.points) || curve.points.length === 0) continue;
            
            // Safely extract points
            const validPoints = [];
            for (const point of curve.points) {
              if (!point || typeof point !== 'object') continue;
              
              const x = typeof point.x === 'number' ? point.x : 
                      (typeof point.x === 'string' ? parseFloat(point.x) : NaN);
              const y = typeof point.y === 'number' ? point.y : 
                      (typeof point.y === 'string' ? parseFloat(point.y) : NaN);
              
              // Only include valid coordinates
              if (!isNaN(x) && !isNaN(y) && x >= 0 && x <= 1 && y >= 0 && y <= 1) {
                validPoints.push({ x, y });
              }
            }
            
            // Only include curves with at least 2 valid points
            if (validPoints.length >= 2) {
              curves.push({
                points: validPoints,
                color: curve.color || '#000000',
                timestamp: typeof curve.timestamp === 'number' ? curve.timestamp : now
              });
            }
          }
        }
        
        // Check to make sure we have a valid user ID
        const userId = grid.userId || this.currentUser?.uid;
        if (!userId) {
          console.warn('Skipping grid without user ID');
          continue;
        }
        
        // Add normalized grid to results
        result.push({
          ...grid,
          id: grid.id,
          timestamp,
          dots,
          curves,
          userId,
          eyeTested: grid.eyeTested || 'unknown'
        });
      } catch (err) {
        console.error('Error normalizing grid:', err);
        // Skip this grid and continue
      }
    }
    
    secureLogger.log(`Normalized ${result.length} valid grid items (filtered out ${grids.length - result.length})`);
    return result;
  }

  /**
   * Helper method to get grids from a specific database path
   * @param path The database path to fetch grids from
   * @returns Promise that resolves to an array of grid data
   */
  private async getGridsFromPath(path: string): Promise<AmslerGridData[]> {
    try {
      secureLogger.log(`getGridsFromPath called for path: ${path}`);
      const gridRef = dbRef(this.db, path);
      secureLogger.log(`Created database reference for path: ${path}`);
      
          const grids: AmslerGridData[] = [];
      
      // Get the snapshot from the reference
      const snapshot = await get(gridRef);
          
          if (!snapshot.exists()) {
        secureLogger.log(`No data found at path: ${path}`);
        return [];
          }
          
      const data = snapshot.val();
      secureLogger.log(`Found data at path ${path}: ${JSON.stringify(Object.keys(data))}`);
      
          snapshot.forEach((childSnapshot) => {
            const gridData = childSnapshot.val();
            
            // Only include valid grid data
            if (gridData && 
                childSnapshot.key && 
                typeof gridData === 'object' && 
                (gridData.timestamp || gridData.eyeTested)) {
              
              // Ensure we have valid arrays for dots and curves
              if (!Array.isArray(gridData.dots)) {
                gridData.dots = [];
              }
              
              if (!Array.isArray(gridData.curves)) {
                gridData.curves = [];
              }
              
          secureLogger.log(`Valid grid data found for key ${childSnapshot.key}`);
              grids.push({
                ...gridData,
                id: childSnapshot.key || '',
              });
            } else {
              console.warn(`Skipping invalid grid data for key ${childSnapshot.key}`);
            }
          });
          
          // Detach the listener after we get the data
      off(gridRef);
      
      secureLogger.log(`Successfully fetched ${grids.length} valid grids from ${path}`);
      return grids;
    } catch (error) {
      secureLogger.error(`Error fetching grids from path ${path}: ${error}`);
      return [];
    }
  }

  public async deleteGrid(gridId: string): Promise<void> {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      // Check if user has premium subscription
      const userRef = dbRef(this.db, `users/${this.currentUser.uid}/subscription`);
      const snapshot = await get(userRef);
      const isPremium = snapshot.exists() && snapshot.val().status === 'active';

      if (!isPremium) {
        throw new Error('Premium subscription required to delete tests');
      }

      // Use the Android app path structure
      const gridRef = dbRef(this.db, `users/${this.currentUser.uid}/grids/${gridId}`);
      await set(gridRef, null);
      secureLogger.log(`Grid ${gridId} deleted successfully`);
    } catch (error) {
      secureLogger.error('Error deleting grid:', error);
      throw error;
    }
  }

  /**
   * Creates a downloadable image of an Amsler grid with the marked anomalies
   * @param grid The grid data to render
   * @returns A data URL for the generated image
   */
  public generateGridImage(grid: AmslerGridData): string {
    try {
      // Define the information text
      const testDate = new Date(grid.timestamp);
      const formattedDate = testDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      const formattedTime = testDate.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
      
      // Eye information
      const eyeText = grid.eyeTested === 'right' ? 'Right Eye' : 
                     grid.eyeTested === 'left' ? 'Left Eye' : 
                     grid.eyeTested === 'both' ? 'Both Eyes' : 'Not Specified';
      
      // Calculate font size based on viewport
      const fontSize = 16; // Increased font size
      const infoTextPadding = 12; // Increased padding around text
      
      // Calculate info section height based on font size plus padding
      const infoSectionHeight = (fontSize * 3) + (infoTextPadding * 4); // Three lines of text plus padding
      
      // Create a canvas element with dynamic height
      const canvas = document.createElement('canvas');
      
      // Set base width (increased for better quality)
      const width = 800; 
      
      // Full height for grid (not reduced)
      const gridHeight = width;
      
      // Total height includes grid height plus info section
      const height = gridHeight + infoSectionHeight;
      
      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        throw new Error('Could not get canvas context');
      }
      
      // Fill background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);
      
      // Grid properties (using full width and calculated grid height)
      const gridSize = 19; // 19 squares (20 points) to match Android implementation
      const cellSizeX = width / gridSize;
      const cellSizeY = gridHeight / gridSize;
      
      // Define the center of the grid (this is the reference point)
      const centerX = width / 2;
      const centerY = gridHeight / 2;
      
      // Prepare anomaly dots
      const distortionAreas = grid.dots ? grid.dots.map(dot => ({
        x: dot.x * width,
        y: dot.y * gridHeight, // Scale to grid height only
        radius: ((cellSizeX + cellSizeY) / 2) * 3 // Average cell size for radius
      })) : [];
      
      // Create grid mesh for distortion
      const gridPointsCount = gridSize + 1;
      const distortedPoints = Array(gridPointsCount).fill(0).map(() => 
        Array(gridPointsCount).fill(0).map(() => ({ x: 0, y: 0 }))
      );
      
      // For each grid point, calculate the distortion with reference to the center
      for (let i = 0; i <= gridSize; i++) {
        for (let j = 0; j <= gridSize; j++) {
          const originalX = j * cellSizeX;
          const originalY = i * cellSizeY;
          
          if (distortionAreas.length > 0) {
            // Apply distortion to the point
            let distortedX = originalX;
            let distortedY = originalY;
            
            for (const area of distortionAreas) {
              const dx = originalX - area.x;
              const dy = originalY - area.y;
              const distance = Math.sqrt(dx * dx + dy * dy);
              
              if (distance < area.radius) {
                // Calculate distortion factor with smoother falloff
                const factor = Math.pow(1 - distance / area.radius, 2.5) * 0.15;
                
                // Calculate the angle from the CENTER of the grid to the current point
                const pointFromCenterX = originalX - centerX;
                const pointFromCenterY = originalY - centerY;
                
                // Calculate the angle from CENTER to the distortion point
                const centerToDotX = area.x - centerX;
                const centerToDotY = area.y - centerY;
                const centerToDotAngle = Math.atan2(centerToDotY, centerToDotX);
                
                // Calculate angle between the center-to-point line and center-to-distortion line
                const pointAngle = Math.atan2(pointFromCenterY, pointFromCenterX);
                const angleDiff = Math.abs(pointAngle - centerToDotAngle);
                
                // Calculate a modifier that increases distortion for points along the same radial line
                const angleModifier = (Math.cos(angleDiff) * 0.5 + 0.5); // 0.5-1.0 range
                
                // Adjust the distortion to curve away from center in a radial pattern
                const adjustedFactor = factor * angleModifier * 2; // Amplify the effect
                const offsetX = Math.cos(centerToDotAngle) * adjustedFactor * cellSizeX * 1.5;
                const offsetY = Math.sin(centerToDotAngle) * adjustedFactor * cellSizeY * 1.5;
                
                distortedX += offsetX;
                distortedY += offsetY;
              }
            }
            
            distortedPoints[i][j] = { x: distortedX, y: distortedY };
          } else {
            // No distortion - straight grid
            distortedPoints[i][j] = { x: originalX, y: originalY };
          }
        }
      }
      
      // Apply smoothing for better rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      // Draw distortion area highlights first with gradient
      if (distortionAreas.length > 0) {
        for (const area of distortionAreas) {
          const gradient = ctx.createRadialGradient(
            area.x, area.y, 0,
            area.x, area.y, area.radius
          );
          gradient.addColorStop(0, 'rgba(150, 0, 0, 0.15)');
          gradient.addColorStop(0.7, 'rgba(150, 0, 0, 0.07)');
          gradient.addColorStop(1, 'rgba(150, 0, 0, 0)');
          
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(area.x, area.y, area.radius, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      
      // Draw grid lines
      ctx.lineWidth = 1;
      ctx.strokeStyle = '#666666'; // Darker grid lines on white background
      
      // Draw horizontal grid lines
      for (let i = 0; i <= gridSize; i++) {
        if (distortionAreas.length > 0) {
          // Draw distorted horizontal lines with special effects in distorted areas
          for (let j = 0; j < gridSize; j++) {
            const start = distortedPoints[i][j];
            const end = distortedPoints[i][j+1];
            
            // Check if this segment is in a distortion area
            const midX = (start.x + end.x) / 2;
            const midY = (start.y + end.y) / 2;
            let inDistortionArea = false;
            
            for (const area of distortionAreas) {
              const dx = midX - area.x;
              const dy = midY - area.y;
              const distance = Math.sqrt(dx * dx + dy * dy);
              
              if (distance < area.radius * 0.8) {
                inDistortionArea = true;
                break;
              }
            }
            
            // Apply blur effect and red color for distorted segments
            if (inDistortionArea) {
              ctx.save();
              ctx.shadowColor = 'rgba(150, 0, 0, 0.4)';
              ctx.shadowBlur = 1.5;
              ctx.strokeStyle = 'rgba(150, 0, 0, 0.9)';
              ctx.lineWidth = 0.8;
            } else {
              ctx.strokeStyle = '#666666';
              ctx.lineWidth = 1;
            }
            
            // Draw line segment
            ctx.beginPath();
            ctx.moveTo(start.x, start.y);
            ctx.lineTo(end.x, end.y);
            ctx.stroke();
            
            if (inDistortionArea) {
              ctx.restore();
            }
          }
        } else {
          // Draw straight horizontal lines
          ctx.beginPath();
          ctx.moveTo(0, i * cellSizeY);
          ctx.lineTo(width, i * cellSizeY);
          ctx.stroke();
        }
      }
      
      // Draw vertical grid lines
      for (let j = 0; j <= gridSize; j++) {
        if (distortionAreas.length > 0) {
          // Draw distorted vertical lines with special effects
          for (let i = 0; i < gridSize; i++) {
            const start = distortedPoints[i][j];
            const end = distortedPoints[i+1][j];
            
            // Check if this segment is in a distortion area
            const midX = (start.x + end.x) / 2;
            const midY = (start.y + end.y) / 2;
            let inDistortionArea = false;
            
            for (const area of distortionAreas) {
              const dx = midX - area.x;
              const dy = midY - area.y;
              const distance = Math.sqrt(dx * dx + dy * dy);
              
              if (distance < area.radius * 0.8) {
                inDistortionArea = true;
                break;
              }
            }
            
            // Apply blur effect and red color for distorted segments
            if (inDistortionArea) {
              ctx.save();
              ctx.shadowColor = 'rgba(150, 0, 0, 0.4)';
              ctx.shadowBlur = 1.5;
              ctx.strokeStyle = 'rgba(150, 0, 0, 0.9)';
              ctx.lineWidth = 0.8;
            } else {
              ctx.strokeStyle = '#666666';
              ctx.lineWidth = 1;
            }
            
            // Draw line segment
            ctx.beginPath();
            ctx.moveTo(start.x, start.y);
            ctx.lineTo(end.x, end.y);
            ctx.stroke();
            
            if (inDistortionArea) {
              ctx.restore();
            }
          }
        } else {
          // Draw straight vertical lines
          ctx.beginPath();
          ctx.moveTo(j * cellSizeX, 0);
          ctx.lineTo(j * cellSizeX, gridHeight);
          ctx.stroke();
        }
      }

      // Draw curves if they exist
      if (grid.curves && grid.curves.length > 0) {
        grid.curves.forEach(curve => {
          if (curve.points && curve.points.length > 0) {
            ctx.beginPath();
            ctx.strokeStyle = curve.color || '#000000';
            ctx.lineWidth = 3;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';

            // Convert normalized coordinates to canvas coordinates
            const firstPoint = curve.points[0];
            const startX = firstPoint.x * width;
            const startY = firstPoint.y * gridHeight;
            ctx.moveTo(startX, startY);

            // Draw the curve
            for (let i = 1; i < curve.points.length; i++) {
              const point = curve.points[i];
              const x = point.x * width;
              const y = point.y * gridHeight;
              ctx.lineTo(x, y);
            }

            ctx.stroke();
          }
        });
      }
      
      // Draw center dot
      ctx.fillStyle = '#000000';
      ctx.beginPath();
      ctx.arc(centerX, centerY, 4, 0, 2 * Math.PI);
      ctx.fill();
      
      // Draw anomaly dots with glow effect
      if (grid.dots && grid.dots.length > 0) {
        for (const dot of grid.dots) {
          if (typeof dot.x === 'number' && typeof dot.y === 'number') {
            // Add glow effect
            ctx.save();
            ctx.shadowColor = 'rgba(150, 0, 0, 0.7)';
            ctx.shadowBlur = 4;
            
            // Draw dot
            ctx.fillStyle = '#960000';
            ctx.beginPath();
            ctx.arc(dot.x * width, dot.y * gridHeight, 3, 0, 2 * Math.PI);
            ctx.fill();
            
            ctx.restore();
          }
        }
      }
      
      // Add a border and background for the info area
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, gridHeight);
      ctx.lineTo(width, gridHeight);
      ctx.stroke();
      
      // Add a light gray background for the info area
      ctx.fillStyle = '#f8f8f8';
      ctx.fillRect(0, gridHeight, width, infoSectionHeight);
      
      // Add information text at the bottom
      ctx.fillStyle = '#000000';
      ctx.font = `bold ${fontSize}px Arial`;
      ctx.textAlign = 'center';
      
      // Date and time
      ctx.fillText(`Date: ${formattedDate} at ${formattedTime}`, width / 2, gridHeight + fontSize + infoTextPadding);
      
      // Eye tested
      ctx.fillText(`Test: ${eyeText}`, width / 2, gridHeight + (fontSize * 2) + (infoTextPadding * 2));
      
      // Anomalies count
      const anomaliesText = grid.dots && grid.dots.length > 0 
        ? `Anomalies detected: ${grid.dots.length}` 
        : 'No anomalies detected';
      ctx.fillText(anomaliesText, width / 2, gridHeight + (fontSize * 3) + (infoTextPadding * 3));
      
      // Return the data URL
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Error generating grid image:', error);
      throw error;
    }
  }

  /**
   * Fetches a grid by ID from the database path structure
   * @param gridId The ID of the grid to fetch
   * @returns Promise that resolves to the grid data or null if not found
   */
  public async getGridById(gridId: string): Promise<AmslerGridData | null> {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      // Use Android app path structure
      const gridRef = dbRef(this.db, `users/${this.currentUser.uid}/grids/${gridId}`);
      const snapshot = await get(gridRef);

      if (snapshot.exists()) {
        const grid = snapshot.val();
        
        // Ensure the grid has valid dots and curves arrays
        if (!Array.isArray(grid.dots)) {
          grid.dots = [];
        }
        
        if (!Array.isArray(grid.curves)) {
          grid.curves = [];
        }
        
        return {
          ...grid,
          id: gridId
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching grid by ID:', error);
      throw error;
    }
  }

  /**
   * Cleans up any invalid grid data in the user's database
   * @returns Promise that resolves when cleanup is complete
   */
  public async cleanupInvalidGridData(): Promise<void> {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      secureLogger.log("Starting cleanup of invalid grid data...");
      
      // Get all grids from the user's path
      const path = `users/${this.currentUser.uid}/grids`;
      const gridsRef = dbRef(this.db, path);
      
      const snapshot = await get(gridsRef);
      if (!snapshot.exists()) {
        secureLogger.log('No grid data to clean up');
        return;
      }
      
      // Find invalid entries
      const invalidEntries: string[] = [];
      snapshot.forEach(childSnapshot => {
        const key = childSnapshot.key;
        const data = childSnapshot.val();
        
        // Check for invalid data
        if (!key || 
            !data || 
            typeof data !== 'object' || 
            !data.timestamp || 
            !data.eyeTested ||
            (data.dots && !Array.isArray(data.dots))) {
          if (key) {
            invalidEntries.push(key);
          }
        }
      });
      
      // Delete invalid entries if found
      if (invalidEntries.length > 0) {
        secureLogger.log(`Found ${invalidEntries.length} invalid grid entries to delete`);
        const deletePromises = invalidEntries.map(id => {
          const invalidRef = dbRef(this.db, `${path}/${id}`);
          return set(invalidRef, null);
        });
        
        await Promise.all(deletePromises);
        secureLogger.log('Successfully deleted all invalid grid entries');
      } else {
        secureLogger.log('No invalid grid entries found');
      }
    } catch (error) {
      secureLogger.error('Error cleaning up invalid grid data:', error);
      throw error;
    }
  }

  /**
   * Deletes all grid data for the current user
   * @returns Promise that resolves when all data is deleted
   */
  public async deleteAllGrids(): Promise<void> {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      const userId = this.currentUser.uid;
      secureLogger.log('Starting deletion of all grid data for user', userId);
      
      // Count the number of tests before deletion to track for free users
      let deletedTestsCount = 0;
      try {
        const primaryRef = dbRef(this.db, `users/${userId}/grids`);
        const snapshot = await get(primaryRef);
        if (snapshot.exists()) {
          deletedTestsCount = Object.keys(snapshot.val()).length;
          secureLogger.log(`Found ${deletedTestsCount} tests to delete`);
        }
      } catch (error) {
        secureLogger.error('Error counting tests before deletion:', error);
      }
      
      // 1. Delete from primary path
      const primaryRef = dbRef(this.db, `users/${userId}/grids`);
      await set(primaryRef, null);
      secureLogger.log('Deleted data from primary path');
      
      // 2. Delete from legacy paths
      const legacyPaths = [
        `users/${userId}/AmslerGridImage`,
        `users/AmslerGridImage/${userId}`,
        `users/${userId}/amslerGridImage`,
        `users/amslerGridImage/${userId}`,
        `users/grids/${userId}`
      ];
      
      for (const path of legacyPaths) {
        try {
          const legacyRef = dbRef(this.db, path);
          await set(legacyRef, null);
          secureLogger.log('Deleted data from legacy path:', path);
        } catch (error) {
          secureLogger.warn('Error deleting from legacy path', path, error);
          // Continue with other paths even if one fails
        }
      }
      
      // 3. Delete from Storage
      try {
        const storage = getStorage();
        const userStorageRef = storageRef(storage, `users/${userId}`);
        const listResult = await listAll(userStorageRef);
        
        // Delete all files in parallel
        await Promise.all(listResult.items.map(async (item) => {
          try {
            await deleteObject(item);
            secureLogger.log('Deleted storage file:', item.fullPath);
          } catch (error) {
            secureLogger.warn('Error deleting storage file', item.fullPath, error);
          }
        }));
      } catch (error) {
        if (error instanceof Error && error.message.includes('storage/object-not-found')) {
          secureLogger.log('No storage files found to delete');
        } else {
          secureLogger.error('Error accessing storage:', error);
        }
        // Continue even if storage deletion fails
      }
      
      // 4. Update last active timestamp and store deleted tests count
      try {
        const userRef = dbRef(this.db, `users/${userId}`);
        const updates: Record<string, any> = {
          'last_active': serverTimestamp()
        };
        
        // Store the deleted tests count for free users to keep track of their quota
        if (deletedTestsCount > 0) {
          // Get current count if it exists
          const statsRef = dbRef(this.db, `users/${userId}/stats`);
          const statsSnapshot = await get(statsRef);
          const currentDeletedCount = statsSnapshot.exists() && statsSnapshot.child('deletedTestsCount').exists() 
            ? statsSnapshot.child('deletedTestsCount').val() || 0 
            : 0;
            
          updates['stats/deletedTestsCount'] = currentDeletedCount + deletedTestsCount;
          updates['stats/lastBulkDeleteTime'] = serverTimestamp();
          secureLogger.log(`Tracking ${deletedTestsCount} deleted tests, total is now ${currentDeletedCount + deletedTestsCount}`);
        }
        
        await update(userRef, updates);
      } catch (error) {
        secureLogger.warn('Error updating user stats:', error);
      }
      
      secureLogger.log('Successfully deleted all grid data');
    } catch (error) {
      secureLogger.error('Error deleting all grid data:', error);
      throw error;
    }
  }

  /**
   * Check and migrate data from the old AmslerGridImage path
   * @returns Promise that resolves when migration is complete
   */
  public async checkAndMigrateOldData(): Promise<boolean> {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    secureLogger.log("Checking for data in legacy format...");
    
    try {
      // Check the old AmslerGridImage path - with correct capitalization
      const oldPath = `users/${this.currentUser.uid}/AmslerGridImage`;
      const oldRef = dbRef(this.db, oldPath);
      const snapshot = await get(oldRef);
      
      if (!snapshot.exists()) {
        secureLogger.log("No legacy data found");
        return false;
      }
      
      secureLogger.log("Found legacy data, starting migration...");
      secureLogger.log(`Legacy data contains ${snapshot.size} entries`);
      
      let migratedCount = 0;
      const migrationPromises: Promise<void>[] = [];
      const deletePromises: Promise<void>[] = [];
      
      // Get the target path for migration
      const newPath = `users/${this.currentUser.uid}/grids`;
      
      // Process each old entry
      snapshot.forEach(childSnapshot => {
        const key = childSnapshot.key;
        const data = childSnapshot.val();
        
        if (!key || !data || typeof data !== 'object') {
          secureLogger.warn(`Skipping invalid legacy data for key ${key}`);
          return;
        }
        
        try {
          // Process the legacy data into the new format
          const newData: Partial<AmslerGridData> = {
            timestamp: data.timestamp || Date.now(),
            eyeTested: data.eyeTested || 'unknown',
            dots: Array.isArray(data.dots) ? data.dots.map((dot: any) => ({
              x: dot.x,
              y: dot.y,
              timestamp: dot.timestamp || Date.now()
            })) : [],
            userId: this.currentUser!.uid,
            migrationSource: 'AmslerGridImage'
          };
          
          // Only migrate if we have minimum valid data
          if (newData.timestamp && newData.eyeTested) {
            secureLogger.log(`Migrating legacy data for key ${key}`);
            
            // Create a new entry in the grids path
            const newRef = dbRef(this.db, `${newPath}/${key}`);
            migrationPromises.push(set(newRef, newData));
            
            // Delete the old entry after successful migration
            const oldItemRef = dbRef(this.db, `${oldPath}/${key}`);
            deletePromises.push(set(oldItemRef, null));
            
            migratedCount++;
          }
        } catch (err) {
          secureLogger.error(`Error processing legacy data for key ${key}:`, err);
        }
      });
      
      // Wait for all migrations to complete
      if (migrationPromises.length > 0) {
        await Promise.all(migrationPromises);
        secureLogger.log(`Successfully migrated ${migratedCount} legacy entries`);
        
        // Delete the old data after successful migration
        secureLogger.log(`Deleting ${deletePromises.length} migrated items from source`);
        for (const deletePromise of deletePromises) {
          try {
            await deletePromise;
          } catch (err) {
            secureLogger.error('Error deleting migrated item:', err);
          }
        }
        secureLogger.log('Deleted migrated data from legacy path');
        
        // Finally, check if the old path is empty and remove it if it is
        const checkRef = dbRef(this.db, oldPath);
        const checkSnapshot = await get(checkRef);
        if (!checkSnapshot.exists() || checkSnapshot.size === 0) {
          await set(checkRef, null);
          secureLogger.log('Removed empty legacy path');
        } else {
          secureLogger.log(`Legacy path still contains ${checkSnapshot.size} entries after migration`);
        }
        
        return true;
      }
      
      return false;
    } catch (error) {
      secureLogger.error('Error during legacy data migration:', error);
      return false;
    }
  }

  /**
   * Targeted cleanup for a specific user path (can be used for debugging)
   * @param specificUserPath The complete path to clean
   * @returns Promise that resolves when cleanup is complete
   */
  public async cleanupSpecificPath(specificUserPath: string): Promise<boolean> {
    try {
      secureLogger.log(`Attempting targeted cleanup of specific path: ${specificUserPath}`);
      
      // Get the reference to the specific path
      const pathRef = dbRef(this.db, specificUserPath);
      
      // First check if the path exists
      const snapshot = await get(pathRef);
      
      if (!snapshot.exists()) {
        secureLogger.log(`Path ${specificUserPath} does not exist or is not accessible`);
        return false;
      }
      
      secureLogger.log(`Found data at ${specificUserPath}, with ${snapshot.size} entries`);
      
      // Try to delete each child individually first
      let successCount = 0;
      let failureCount = 0;
      
      if (snapshot.hasChildren()) {
        const deletePromises: Array<Promise<void>> = [];
        
        snapshot.forEach(childSnapshot => {
          if (childSnapshot.key) {
            const childPath = `${specificUserPath}/${childSnapshot.key}`;
            secureLogger.log(`Attempting to delete: ${childPath}`);
            
            const childRef = dbRef(this.db, childPath);
            const deletePromise = set(childRef, null)
              .then(() => {
                secureLogger.log(`Successfully deleted: ${childPath}`);
                successCount++;
              })
              .catch(err => {
                secureLogger.error(`Error deleting ${childPath}:`, err);
                failureCount++;
                return Promise.resolve(); // Continue with other deletions
              });
            
            deletePromises.push(deletePromise);
          }
        });
        
        // Wait for all delete operations to complete
        await Promise.all(deletePromises);
        secureLogger.log(`Deletion results: ${successCount} successful, ${failureCount} failed`);
      }
      
      // After deleting children, try to delete the entire path
      if (failureCount === 0) {
        try {
          await set(pathRef, null);
          secureLogger.log(`Successfully deleted entire path: ${specificUserPath}`);
          return true;
        } catch (err) {
          secureLogger.error(`Error deleting entire path ${specificUserPath}:`, err);
          return successCount > 0; // Return true if we at least deleted some children
        }
      } else {
        secureLogger.log(`Some deletions failed, not attempting to delete entire path`);
        return successCount > 0;
      }
    } catch (error) {
      secureLogger.error(`Error in targeted cleanup for ${specificUserPath}:`, error);
      return false;
    }
  }

  /**
   * Cleans up any legacy data paths
   */
  public async cleanupLegacyData(): Promise<void> {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      secureLogger.log("Starting cleanup of legacy data paths...");
      
      // List of legacy paths to check and clean - with correct capitalization
      // Only include paths that the user has permission to access based on security rules
      const legacyPaths = [
        `users/${this.currentUser.uid}/AmslerGridImage`, // User has permission to this path
        `users/${this.currentUser.uid}/amslerGridImage`, // User has permission to this path
        `users/grids/${this.currentUser.uid}` // Old web path (may not have permission)
      ];
      
      // Skip paths that are likely to cause permission issues
      // users/AmslerGridImage/$uid and users/amslerGridImage/$uid will fail due to security rules
      
      // Process paths in sequence to avoid overwhelming the database
      for (const path of legacyPaths) {
        try {
          secureLogger.log(`Checking legacy path: ${path}`);
          const pathRef = dbRef(this.db, path);
          const snapshot = await get(pathRef);
            
            if (snapshot.exists()) {
              secureLogger.log(`Found legacy data at ${path}, with ${snapshot.size} entries, cleaning...`);
              
              try {
                await set(pathRef, null);
                secureLogger.log(`Successfully removed entire path: ${path}`);
            } catch (pathError) {
              secureLogger.warn(`Could not remove path: ${path}`, pathError);
              // Skip individual entry cleanup to avoid more errors
              }
            } else {
              secureLogger.log(`No legacy data found at ${path}`);
            }
          } catch (pathError) {
          // Just log and continue if we hit a permission error
          secureLogger.warn(`Skipping inaccessible path ${path} due to: ${(pathError as Error).message}`);
        }
      }
      
      secureLogger.log("Legacy path cleanup complete");
      
    } catch (error) {
      secureLogger.error('Error in legacy data cleanup:', error);
      // Don't throw the error - we don't want the app to crash if cleanup fails
      // Just log it and let the app continue
    }
  }

  /**
   * Checks if the database is empty for the current user
   * @returns Promise that resolves to true if the database is empty, false otherwise
   */
  public async isDatabaseEmpty(): Promise<boolean> {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      // Only check canonical path
      const canonicalPath = `users/${this.currentUser.uid}/grids`;
      const canonicalRef = dbRef(this.db, canonicalPath);
      const canonicalSnapshot = await get(canonicalRef);
      
      if (canonicalSnapshot.exists()) {
        const snapshotValue = canonicalSnapshot.val();
        if (snapshotValue && typeof snapshotValue === 'object' && Object.keys(snapshotValue).length > 0) {
          secureLogger.log('Found data in canonical path');
          return false;
        }
      }
      
      // If canonical path is empty
      secureLogger.log('All database paths are empty');
      return true;
    } catch (error) {
      secureLogger.error('Error checking if database is empty:', error);
      return false;
    }
  }
}

export default FirebaseService; 